import { MutableRefObject, useEffect, useState } from 'react'
import {
  GridColDef,
  GridColumnGroupingModel,
  GridPinnedColumnFields,
} from '@microservices/wiskey-react-table-component'
import { Interpolation, Theme } from '@mui/material'

import { getColumnsByObject } from '@components/TimeLineGrid/helpers/getColumnsByObject'
import { getColumnsByTimeInterval } from '@components/TimeLineGrid/helpers/getColumnsByTimeInterval'

import { TIME_LINE_MODE } from '@constants'
import { ObjectDataRecord, RestrictionData, TimelineCellContext, TimeLineDTOType } from '@types'

import { DateInterval, enrichConfigColumnsWithRestrictions, getColumns } from '../helpers'
import { useHandlers } from '../hooks'

type UseColumnsParams = {
  config: TimeLineDTOType
  dateIntervals: DateInterval[]
  isLoadingObjectYRestrictions: boolean
  isLoadingObjectXRestrictions: boolean
  objectYRestrictions: RestrictionData
  objectXRestrictions: RestrictionData
  handlers: ReturnType<typeof useHandlers>
  cellContextRef: MutableRefObject<TimelineCellContext>
  columnsObjectData: ObjectDataRecord[]
}

export const useColumns = ({
  config,
  dateIntervals,
  isLoadingObjectYRestrictions,
  isLoadingObjectXRestrictions,
  objectYRestrictions,
  objectXRestrictions,
  handlers,
  cellContextRef,
  columnsObjectData,
}: UseColumnsParams) => {
  const [columns, setColumns] = useState<GridColDef[]>([])
  const [columnsStyles, setColumnsStyles] = useState<Interpolation<Theme>>({})
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({
    left: [],
    right: [],
  })
  const [columnGroupingModel, setColumnGroupingModel] = useState<GridColumnGroupingModel>([])
  const {
    data: { objectXPKField, objectYPKField },
  } = handlers

  const getDynamicColumns = () => {
    if (config.mode === TIME_LINE_MODE.DATE_TIME) {
      return getColumnsByTimeInterval({
        dateIntervals,
        handlers,
        cellContextRef,
        config,
      })
    }

    if (config.mode === TIME_LINE_MODE.OBJECT) {
      return getColumnsByObject({
        config,
        handlers,
        cellContextRef,
        columnsObjectData,
      })
    }

    return []
  }

  useEffect(() => {
    if (config && !isLoadingObjectYRestrictions && !isLoadingObjectXRestrictions) {
      const dynamicColumns = getDynamicColumns()

      cellContextRef.current = {
        ...cellContextRef.current,
        dynamicColumnIds: dynamicColumns.map(c => String(c.field)),
      }

      const { columns, styles, pinnedColumns, columnGroupingModel } = getColumns({
        config: enrichConfigColumnsWithRestrictions({
          config,
          objectYRestrictions,
          objectXRestrictions,
        }),
        dynamicColumns,
        handlers,
        cellContextRef,
      })

      setColumns(columns)
      setColumnsStyles(styles)
      setPinnedColumns(pinnedColumns)
      setColumnGroupingModel(columnGroupingModel)
    }
  }, [
    isLoadingObjectYRestrictions,
    isLoadingObjectXRestrictions,
    objectYRestrictions,
    objectXRestrictions,
    dateIntervals,
    config,
    objectXPKField,
    objectYPKField,
    columnsObjectData,
  ])

  return {
    columns,
    columnsStyles,
    pinnedColumns,
    columnGroupingModel,
  }
}
